import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdRefresh, MdEdit, MdShare, MdDownload, MdClose, MdTextFields, MdArrowBack } from 'react-icons/md';
import ActionBar from './ActionBar';
import type { Action } from './ActionBar';
import FlyerPreview from './FlyerPreview';
import FlyerDetails from './FlyerDetails';
import EditCaptionsPanel from './EditCaptionsPanel';
import { ShareModal } from '../scheduleCampaign';
import { fetchIntegrationsRequest } from '../../redux/slices/integrationsSlice';
import { resetContentGeneration } from '../../redux/slices/contentGenerationSlice';

import LoadingState from '../common/LoadingState';
import type { RootState } from '../../redux/store';
import type { ScheduleData } from '../scheduleCampaign';
import type { FlyerData } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { regenerateFlyer } from '../../api/contentApi';
import { useSSETaskMonitor } from '../../hooks/useSSETaskMonitor';
import { PROMOFLOW_BASE_API_URL } from '../../api/endPoints';

const Playground: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const [searchParams] = useSearchParams();
  
  // Get campaign data from location state (passed from CampaignGenerator)
  const { campaignData: initialCampaignData } = location.state || {};
  
  const [campaignData, setCampaignData] = useState<any>(initialCampaignData || null);
  const [fetchingCampaign, setFetchingCampaign] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCaptionsPanel, setShowCaptionsPanel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [taskMonitorUrl, setTaskMonitorUrl] = useState<string | null>(null);
  const [regenerateProgress, setRegenerateProgress] = useState<number>(0);

  // Track if we've already fetched to prevent duplicate calls (even in StrictMode)
  const hasFetched = useRef(false);
  
  // SSE Task Monitor for regeneration
  const { status: taskStatus, message: taskMessage, isMonitoring } = useSSETaskMonitor({
    monitorUrl: taskMonitorUrl,
    enabled: !!taskMonitorUrl,
    onCompleted: async () => {
      // When regeneration completes, fetch updated flyer details
      try {
        if (flyerData) {
          const axios = (await import('axios')).default;
          const flyerUrl = `${PROMOFLOW_BASE_API_URL.triggerUrl}/flyers/${restaurantId}?content_id=${flyerData.id}`;
          const flyerResponse = await axios.get(flyerUrl);
          const flyerDetails = flyerResponse.data;
          
          if (flyerDetails && flyerDetails.length > 0) {
            const updatedFlyer = flyerDetails[0];
            setFlyerData({
              ...flyerData,
              imageUrl: updatedFlyer.flyer_url || flyerData.imageUrl
            });
          }
        }
      } catch (error) {
        console.error('Error fetching updated flyer:', error);
      } finally {
        setTaskMonitorUrl(null);
        setRegenerateProgress(0);
        setIsRegenerating(false); // Reset regenerating state
      }
    },
    onFailed: () => {
      setTaskMonitorUrl(null);
      setRegenerateProgress(0);
      setIsRegenerating(false); // Reset regenerating state
    }
  });
  
  // Update progress based on task status
  useEffect(() => {
    if (isMonitoring) {
      switch (taskStatus) {
        case 'processing':
          setRegenerateProgress(50);
          break;
        case 'extracting':
          setRegenerateProgress(75);
          break;
        case 'completed':
          setRegenerateProgress(100);
          break;
        default:
          setRegenerateProgress(25);
      }
    }
  }, [taskStatus, isMonitoring]);

  // Get data from Redux store
  const { generatedContent, flyerDetails, loading, ideaId: reduxIdeaId, ideaType: reduxIdeaType } = useSelector(
    (state: RootState) => state.contentGeneration
  );
  
  // Get campaignId and campaignType from URL params (new campaign flow)
  const campaignId = searchParams.get('campaignId');
  const campaignType = searchParams.get('campaignType');
  
  // Get ideaId and ideaType from URL params (old flow - fallback to Redux if not in URL)
  const ideaId = searchParams.get('ideaId') ? Number(searchParams.get('ideaId')) : reduxIdeaId;
  const ideaType = searchParams.get('ideaType') || reduxIdeaType;

  // Parse campaign data from GET API (NEW FLOW)
  const flyerDataFromCampaign = useMemo(() => {
    if (!campaignData) return null;

    console.log('[Playground] Parsing campaign data:', campaignData);

    const { campaign, flyer, mediaCaption } = campaignData;

    // Extract flyer image URL
    const flyerImageUrl = campaign.flyer?.image?.storage_url || flyer?.mediaType?.[0]?.image?.storage_url || '';

    // Parse social media captions from mediaCaption
    const socialCaptions: Record<string, import('./types').SocialMediaContent> = {};
    
    if (mediaCaption?.platforms && mediaCaption.platforms.length > 0) {
      const channels = mediaCaption.platforms[0].channels;
      
      Object.keys(channels).forEach((platform) => {
        const channelData = channels[platform];
        socialCaptions[platform] = {
          post_copy: channelData.post_copy || '',
          subject_line: channelData.subject_line, // For Email
          visual_brief: channelData.visual_brief
        };
      });
    }

    return {
      id: campaign.id,
      imageUrl: flyerImageUrl,
      headline: campaign.title || '',
      description: campaign.description || '',
      offerDetails: campaign.offerDetails || '',
      footer: campaign.tagLine || '',
      prompt: '', // Not available in campaign data
      socialCaptions,
      contentIds: {} // Not needed for campaign data
    };
  }, [campaignData]);

  // Parse and build flyer data from API response (OLD FLOW)
  const flyerDataFromAPI = useMemo(() => {
    if (!generatedContent || !flyerDetails) return null;

    // Parse seasonal flyer content payload
    let flyerContent: any = {};
    if (generatedContent.seasonal_flyer && generatedContent.seasonal_flyer.length > 0) {
      try {
        flyerContent = JSON.parse(generatedContent.seasonal_flyer[0].content_payload);
      } catch (error) {
        console.error('Error parsing flyer content:', error);
      }
    }

    // Parse social media captions with full details - dynamically capture all platforms
    const socialCaptions: Record<string, import('./types').SocialMediaContent> = {};
    const contentIds: Record<string, string> = {}; // Map platform to content_id
    
    if (generatedContent.social_media) {
      generatedContent.social_media.forEach((item) => {
        try {
          const payload = JSON.parse(item.content_payload);
          const channel = item.primary_channel;
          
          // Debug: Log the item to see what fields are available
          console.log('Social Media Item:', item);
          console.log('Content ID:', item.content_id);
          console.log('Item keys:', Object.keys(item));
          
          // Store content ID for this platform - check if there's a uuid field
          const itemAny = item as any;
          contentIds[channel] = itemAny.uuid || itemAny.content_uuid || item.content_id?.toString() || '';
          
          // Store all platforms with full content details
          socialCaptions[channel] = {
            post_copy: payload.post_copy || '',
            subject_line: payload.subject_line, // For Email
            visual_brief: payload.visual_brief ? {
              image_prompt: payload.visual_brief.image_prompt || '',
              image_style: payload.visual_brief.image_style || '',
              aspect_ratio: payload.visual_brief.aspect_ratio || '',
              video_idea: payload.visual_brief.video_idea || ''
            } : undefined
          };
        } catch (error) {
          console.error('Error parsing social media content:', error);
        }
      });
    }

    return {
      id: flyerDetails.content_id?.toString() || '1',
      imageUrl: flyerDetails.flyer_url || '',
      headline: flyerContent.Header || '',
      description: flyerContent.Description || '',
      offerDetails: flyerContent['Offer Details'] || '',
      footer: flyerContent.Footer || '',
      prompt: flyerContent.hero_image_prompt || '',
      socialCaptions,
      contentIds // Add contentIds to the returned object
    };
  }, [generatedContent, flyerDetails]);

  // Use flyerDataFromAPI directly, no fallback to mock data
  const [flyerData, setFlyerData] = useState<FlyerData | null>(null);

  // Fetch campaign data if campaignId is in URL params (new flow)
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!campaignId || !restaurantId || initialCampaignData) return; // Skip if already have data
      
      setFetchingCampaign(true);
      try {
        const axios = (await import('axios')).default;
        const ENDPOINTS = (await import('../../api/endPoints')).default;
        
        const response = await axios.get(ENDPOINTS.getCampaign(campaignId), {
          headers: {
            'restaurant-id': restaurantId,
          },
        });
        
        if (response.data.success && response.data.data) {
          console.log('[Playground] Campaign data fetched:', response.data.data);
          setCampaignData(response.data.data);
        }
      } catch (error) {
        console.error('[Playground] Error fetching campaign:', error);
      } finally {
        setFetchingCampaign(false);
      }
    };
    
    fetchCampaignData();
  }, [campaignId, restaurantId, initialCampaignData]);

  // Update flyerData - prioritize campaign data over legacy API data
  useEffect(() => {
    if (flyerDataFromCampaign) {
      console.log('[Playground] Using campaign data');
      setFlyerData(flyerDataFromCampaign);
    } else if (flyerDataFromAPI) {
      console.log('[Playground] Using legacy API data');
      setFlyerData(flyerDataFromAPI);
    }
  }, [flyerDataFromCampaign, flyerDataFromAPI]);

  // Fetch integrations on mount only once
  useEffect(() => {
    // Only fetch if we haven't fetched before and restaurantId is available
    if (!hasFetched.current && restaurantId) {
      hasFetched.current = true;
      dispatch(fetchIntegrationsRequest({ restaurantId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]); // Add restaurantId to deps

  // Cleanup: Reset content generation state when leaving the page
  // Commented out to allow smooth back navigation without loading screen
  // useEffect(() => {
  //   return () => {
  //     // This runs when the component unmounts
  //     dispatch(resetContentGeneration());
  //   };
  // }, [dispatch]);

  const handleRegenerateFlyer = async () => {
    if (!flyerData || !restaurantId) return;
    
    try {
      setIsRegenerating(true);
      
      // Prepare flyer content from current flyerData
      const flyerContent = {
        Header: flyerData.headline,
        Description: flyerData.description,
        'Offer Details': flyerData.offerDetails,
        Footer: flyerData.footer,
        hero_image_prompt: flyerData.prompt
      };
      
      // Step 1: Call edit API to update the flyer
      const editResponse = await regenerateFlyer(
        flyerData.id,
        restaurantId,
        flyerContent
      );
      
      console.log('Flyer edit API response:', editResponse);
      
      // Step 2: Call flyers POST API after edit success
      const axios = (await import('axios')).default;
      const flyersApiUrl = `${PROMOFLOW_BASE_API_URL.triggerUrl}/flyers/${restaurantId}`;
      
      const flyersPayload = {
        flyer_id: flyerData.id, // Use the same flyer_id from edit call
        flyer_content: "", // Empty as per requirement
        idea_id: ideaId || null // Use ideaId from state
      };
      
      console.log('Calling flyers POST API with payload:', flyersPayload);
      
      const flyersResponse = await axios.post(flyersApiUrl, flyersPayload);
      
      console.log('Flyers API response:', flyersResponse.data);
      
      // Step 3: Start task monitor only after flyers API succeeds
      if (ideaId && ideaType) {
        // Build task monitor URL with correct format
        const monitorUrl = `${PROMOFLOW_BASE_API_URL.triggerUrl}/task-monitor?restaurantId=${restaurantId}&ideaId=${ideaId}&ideaType=${ideaType}`;
        console.log('Starting task monitor with URL:', monitorUrl);
        setTaskMonitorUrl(monitorUrl);
        setRegenerateProgress(10);
      } else {
        console.warn('No idea_id or idea_type available in Redux state');
        console.warn('Cannot start task monitoring - missing required parameters');
      }
      
    } catch (error) {
      console.error('Error regenerating flyer:', error);
      // Could add error toast notification here
      setIsRegenerating(false);
    } finally {
      // Don't set isRegenerating to false here - let task monitor completion handle it
      // setIsRegenerating(false);
    }
  };

  const handleEditDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleEditCaptions = () => {
    setShowCaptionsPanel(true);
  };

  const handleSaveCaptions = (captions: any) => {
    if (flyerData) {
      setFlyerData({
        ...flyerData,
        socialCaptions: captions
      });
    }
    console.log('Captions saved:', captions);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handlePostNow = () => {
    console.log('Posting campaign now...');
    // Add post now logic here
  };

  const handleSchedule = (data: ScheduleData) => {
    console.log('Scheduling campaign:', data);
    // Add schedule logic here
  };

  const handleDownload = async () => {
    if (!flyerData || !flyerData.imageUrl) {
      console.error('No flyer image available to download');
      return;
    }

    try {
      console.log('Starting download for:', flyerData.imageUrl);
      
      // Import axios
      const axios = (await import('axios')).default;
      
      // Fetch the image as blob using axios with credentials
      const response = await axios.get(flyerData.imageUrl, {
        responseType: 'blob',
        withCredentials: false,
        headers: {
          'Accept': 'image/*'
        }
      });
      
      // Create blob URL
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename - use flyer title or default name
      const filename = flyerData.headline 
        ? `${flyerData.headline.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flyer.jpeg`
        : 'flyer.jpeg';
      link.download = filename;
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('Flyer downloaded successfully:', filename);
    } catch (error) {
      console.error('Error downloading flyer:', error);
      
      // Fallback: Try opening in new tab if download fails
      console.log('Attempting fallback: opening in new tab');
      window.open(flyerData.imageUrl, '_blank');
    }
  };

  // Custom actions for the action bar
  const actions: Action[] = [
    {
      id: 'edit',
      icon: <MdEdit size={20} />,
      label: 'Edit Flyer',
      onClick: handleEditDetails
    },
    {
      id: 'edit-captions',
      icon: <MdTextFields size={20} />,
      label: 'Edit Captions',
      onClick: handleEditCaptions
    },
    {
      id: 'share',
      icon: <MdShare size={20} />,
      label: 'Share',
      onClick: handleShare
    },
    {
      id: 'download',
      icon: <MdDownload size={20} />,
      label: 'Download',
      onClick: handleDownload
    }
  ];

  // Show loading state until data is ready
  if (loading || fetchingCampaign || !flyerData) {
    return <LoadingState message={fetchingCampaign ? "Fetching campaign details..." : "Loading flyer content..."} />;
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Header Row: Back Button (Left) + Action Bar (Center) + Close Button */}
        <div className="mb-8 flex items-center justify-between gap-4">
          {/* Back Button - Left Side */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              dispatch(resetContentGeneration());
              navigate(`/${restaurantId}/campaign-ideas`);
            }}
            className="px-3 py-2 cursor-pointer text-white font-medium rounded-lg flex items-center gap-2 transition-colors backdrop-blur-sm border border-gray-600/30"
            style={{
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 14px 0 #0000001a',
              background: '#212121',
              border: '1px solid #e0e0e021'
            }}
          >
            <MdArrowBack size={20} />
          </motion.button>

          {/* Action Bar - Center */}
          <div className="flex items-center gap-4">
            <ActionBar actions={actions} className="" />
            
            {/* Close Edit Button - Next to Action Bar */}
            <AnimatePresence>
              {showDetails && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleEditDetails}
                  className="p-3 bg-[#212121] cursor-pointer hover:bg-gray-700/80 text-white font-medium rounded-full flex items-center justify-center gap-2 transition-colors backdrop-blur-sm shadow-lg border border-gray-600/30"
                >
                  <MdClose size={20} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Empty div for spacing balance */}
          <div className="w-[60px]"></div>
        </div>

        {/* Main Content */}
        <div className={`relative grid gap-8 items-start transition-all duration-500 ${
          showDetails ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
        }`}>
          {/* Flyer Preview - Centered when details hidden, left side when details shown */}
          <motion.div 
            className={`flex flex-col items-center space-y-6 ${
              !showDetails ? 'mx-auto' : ''
            }`}
            layout
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <div className="relative">
              <FlyerPreview imageUrl={flyerData.imageUrl} />
              
              {/* Regeneration Progress Overlay */}
              <AnimatePresence>
                {isMonitoring && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center"
                  >
                    <div className="text-center space-y-4">
                      <div className="relative w-24 h-24">
                        <svg className="w-24 h-24 transform -rotate-90">
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="6"
                            fill="none"
                          />
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="#EE4423"
                            strokeWidth="6"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 40}`}
                            strokeDashoffset={`${2 * Math.PI * 40 * (1 - regenerateProgress / 100)}`}
                            strokeLinecap="round"
                            className="transition-all duration-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white text-lg font-semibold">{regenerateProgress}%</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-white font-medium text-lg">
                          {regenerateProgress < 30 ? 'Starting generation...' :
                           regenerateProgress < 60 ? 'Processing flyer...' :
                           regenerateProgress < 90 ? 'Finalizing...' :
                           'Almost done!'}
                        </p>
                        {taskMessage && (
                          <p className="text-gray-400 text-sm mt-1 capitalize">{taskMessage}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-2">This may take a few moments</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right: Flyer Details - Animated entry */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="bg-gray-900/50 rounded-md p-4 flex flex-col max-h-[calc(90vh-180px)] overflow-hidden"
                style={{
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 0 14px 0 #0000001a',
                  background: 'linear-gradient(90deg, #212121eb, #373737eb)',
                  border: '1px solid #e0e0e021'
                }}
              >
                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                  <FlyerDetails 
                    data={flyerData!}
                    onEdit={(field, value) => {
                      if (flyerData) {
                        setFlyerData({
                          ...flyerData,
                          [field]: value
                        });
                      }
                    }}
                  />
                </div>
                
                {/* Regenerate Button - Only shown when details are visible */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ scale: isRegenerating ? 1 : 1.05 }}
                  whileTap={{ scale: isRegenerating ? 1 : 0.95 }}
                  onClick={handleRegenerateFlyer}
                  disabled={isRegenerating}
                  className={`mt-4 px-8 py-3 text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-colors flex-shrink-0 ${
                    isRegenerating 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-[#EE4423] hover:bg-[#d63d20]'
                  }`}
                >
                  <MdRefresh size={24} className={isRegenerating ? 'animate-spin' : ''} />
                  {isRegenerating ? 'Regenerating...' : 'Regenerate Flyer'}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Edit Captions Panel */}
      {restaurantId && (
        <EditCaptionsPanel
          isOpen={showCaptionsPanel}
          onClose={() => setShowCaptionsPanel(false)}
          captions={flyerData.socialCaptions || {}}
          contentIds={flyerData.contentIds || {}}
          restaurantId={restaurantId}
          onSave={handleSaveCaptions}
        />
      )}

      {/* Share Modal */}
      {restaurantId && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          restaurantId={restaurantId}
          flyerImageUrl={flyerData.imageUrl}
          generatedContentId={flyerDetails?.content_id ? Number(flyerDetails.content_id) : undefined}
          flyerId={flyerData.id ? Number(flyerData.id) : undefined}
          onPostNow={handlePostNow}
          onSchedule={handleSchedule}
        />
      )}
    </div>
  );
};

export default Playground;